import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

const SITE = "https://artlenss.com";
const DEFAULT_IMAGE = "https://artlenss.com/brand/og-cover.jpg";

// Static per-route SEO metadata (bilingual). Dynamic routes handled below.
const META = {
  "/": {
    en: {
      title: "ARTLENSS — Cinematic Production Studio in Jeddah",
      description: "A cinematic production studio in Jeddah, Saudi Arabia. Commercials, documentaries, drama, product photography, and motion graphics — engineered end-to-end.",
    },
    ar: {
      title: "ARTLENSS — استوديو الإنتاج السينمائي في جدة",
      description: "استوديو إنتاج سينمائي في جدة، المملكة العربية السعودية. إعلانات ووثائقيات ودراما وتصوير منتجات وموشن جرافيك بجودة سينمائية متكاملة.",
    },
  },
  "/about": {
    en: { title: "About ARTLENSS — Studio & Team", description: "Meet the ARTLENSS crew — 25+ years engineering cinematic stories from Saudi Arabia to the world." },
    ar: { title: "من نحن — استوديو ARTLENSS", description: "تعرّف على فريق ARTLENSS — أكثر من 25 عاماً في هندسة القصص السينمائية من المملكة إلى العالم." },
  },
  "/services": {
    en: { title: "Services — ARTLENSS Cinematic Production", description: "From concept to delivery: commercials, documentaries, drama, motion graphics, product photography, and sound design." },
    ar: { title: "خدماتنا — إنتاج سينمائي متكامل | ARTLENSS", description: "من الفكرة إلى التسليم: إعلانات ووثائقيات ودراما وموشن جرافيك وتصوير منتجات وتصميم صوت." },
  },
  "/work": {
    en: { title: "Our Work — ARTLENSS Portfolio", description: "Selected films, commercials, and documentaries produced by ARTLENSS across the Kingdom of Saudi Arabia and the Arab region." },
    ar: { title: "أعمالنا — معرض ARTLENSS", description: "مختارات من أفلامنا وإعلاناتنا ووثائقياتنا التي أنتجناها في المملكة العربية السعودية والمنطقة العربية." },
  },
  "/methodology": {
    en: { title: "Our Methodology — How ARTLENSS Makes Work", description: "Idea → Script → Storyboard → Pre-production → Production → Post. The cinematic backbone of every ARTLENSS project." },
    ar: { title: "منهجيتنا — كيف نصنع الأعمال في ARTLENSS", description: "الفكرة → السيناريو → الستوري بورد → ما قبل الإنتاج → الإنتاج → ما بعد الإنتاج. العمود الفقري السينمائي لكل مشروع." },
  },
  "/awards": {
    en: { title: "Awards & Recognition — ARTLENSS", description: "Awards, recognitions, and press features earned by ARTLENSS across a quarter century of cinematic storytelling." },
    ar: { title: "الجوائز والتقدير — ARTLENSS", description: "الجوائز والتكريمات والتغطيات الصحفية التي حصلت عليها ARTLENSS على مدار ربع قرن من العمل السينمائي." },
  },
  "/blog": {
    en: { title: "ARTLENSS Blog — Craft, Insights & Behind the Scenes", description: "Articles on cinematography, directing, editing, and the craft of visual storytelling from the ARTLENSS studio." },
    ar: { title: "مدونة ARTLENSS — الحرفة والرؤى وخلف الكواليس", description: "مقالات في التصوير السينمائي والإخراج والمونتاج وفنّ السرد البصري من داخل استوديو ARTLENSS." },
  },
  "/contact": {
    en: { title: "Contact ARTLENSS — Start a Project", description: "Book your cinematic project with ARTLENSS. Jeddah studio · +966 55 414 2007 · info@artlenss.com" },
    ar: { title: "تواصل معنا — ابدأ مشروعك السينمائي | ARTLENSS", description: "احجز مشروعك السينمائي مع ARTLENSS. استوديو جدة · +٩٦٦ ٥٥ ٤١٤ ٢٠٠٧ · info@artlenss.com" },
  },
  "/privacy": {
    en: { title: "Privacy Policy — ARTLENSS", description: "How ARTLENSS collects, uses, and protects your data. Full bilingual privacy policy." },
    ar: { title: "سياسة الخصوصية — ARTLENSS", description: "كيف تجمع ARTLENSS بياناتك وتستخدمها وتحميها. سياسة خصوصية كاملة ثنائية اللغة." },
  },
  "/terms": {
    en: { title: "Terms of Service — ARTLENSS", description: "Terms governing the use of ARTLENSS services and website." },
    ar: { title: "الشروط والأحكام — ARTLENSS", description: "الشروط الحاكمة لاستخدام خدمات وموقع ARTLENSS." },
  },
};

const setMeta = (name, value, attr = "name") => {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}='${name}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setLinkTag = (rel, href, extra = {}) => {
  if (!href) return;
  const selector = `link[rel='${rel}']${Object.entries(extra).map(([k, v]) => `[${k}='${v}']`).join("")}`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SeoManager = () => {
  const { pathname } = useLocation();
  const params = useParams();
  const { lang } = useLang();

  useEffect(() => {
    // Normalize path — strip trailing slash (except for root)
    let normalized = pathname.replace(/\/+$/, "");
    if (!normalized) normalized = "/";

    // Resolve base path (strip dynamic segments so /work/:slug and /blog/:slug reuse parent config)
    let matchKey = normalized;
    if (normalized.startsWith("/work/") && params.slug) matchKey = "/work";
    if (normalized.startsWith("/blog/") && params.slug) matchKey = "/blog";

    const cfg = META[matchKey]?.[lang] || META["/"][lang];
    const canonical = `${SITE}${normalized === "/" ? "/" : normalized}`;

    // Title
    if (cfg?.title) document.title = cfg.title;

    // Canonical (deduplicated)
    setLinkTag("canonical", canonical);

    // Description
    if (cfg?.description) setMeta("description", cfg.description);

    // Open Graph
    setMeta("og:title", cfg?.title, "property");
    setMeta("og:description", cfg?.description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", "ARTLENSS", "property");
    setMeta("og:image", DEFAULT_IMAGE, "property");
    setMeta("og:locale", lang === "ar" ? "ar_SA" : "en_US", "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", cfg?.title);
    setMeta("twitter:description", cfg?.description);
    setMeta("twitter:image", DEFAULT_IMAGE);

    // hreflang alternates for AR/EN of same URL
    setLinkTag("alternate", canonical, { hreflang: "ar" });
    setLinkTag("alternate", canonical, { hreflang: "en" });
    setLinkTag("alternate", canonical, { hreflang: "x-default" });

    // Language attribute on <html>
    document.documentElement.setAttribute("lang", lang === "ar" ? "ar" : "en");
  }, [pathname, params.slug, lang]);

  return null;
};

export default SeoManager;
